/* eslint-disable no-nested-ternary, react/jsx-props-no-spreading, react/jsx-no-useless-fragment */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { useParseDates } from '../../hooks/useDates';
import * as AppPropTypes from '../../lib/PropTypes';

import { useModal } from '../../contexts/ModalsContext';
import AddToFavoritesButton from '../buttons/AddToFavorites';
import TicketButton from '../buttons/Ticket';
import CircledPlus from '../icons/CircledPlus';
import CardLayout from '../partials/CardLayout';
import CardTags from '../partials/CardTags';
import Date from '../partials/Date';
import DateRange from '../partials/DateRange';
import FilmTitle from '../partials/FilmTitle';
import IconLink from '../partials/IconLink';
import Link from '../partials/Link';
import Time from '../partials/Time';

import styles from '../../../styles/cards/event.module.scss';

const propTypes = {
    item: AppPropTypes.event,
    showDateRange: PropTypes.bool,
    isLink: PropTypes.bool,
    isModal: PropTypes.bool,
    withFavoriteButton: PropTypes.bool,
    withYear: PropTypes.bool,
    openFilmModal: PropTypes.func,
    placeholderIndex: PropTypes.number,
    className: PropTypes.string,
};

const defaultProps = {
    item: null,
    showDateRange: false,
    isLink: true,
    isModal: false,
    withFavoriteButton: true,
    withYear: false,
    openFilmModal: null,
    placeholderIndex: null,
    className: null,
};

const EventCard = ({
    item,
    showDateRange,
    isLink,
    isModal,
    withFavoriteButton,
    withYear,
    openFilmModal,
    placeholderIndex,
    className,
}) => {
    const {
        type,
        title,
        url: eventUrl = null,
        ticketsUrl = null,
        film = null,
        filmBundle = null,
        image = null,
        startDate: itemStartDate = null,
        endDate: itemEndDate = null,
        venue = null,
        isOnline = false,
        isPurchasable = false,
        isUpcoming = false,
        isForum = false,
        product = null,
        isFree = false,
        isInvitationOnly = false,
        edition = null,
        qa = null,
        subtitleLanguages = null,
    } = item;

    const { url: filmUrl = null, subtitleLanguages: filmSubtitles = null } = film || {};
    const { handle: year = null } = edition || {}; // Beware // tickets = null
    const isFilm = filmUrl !== null;
    const url = isFilm ? filmUrl : eventUrl;
    const { type: productType = null } = product || {};
    const showDate = itemStartDate !== null;

    const { films: bundleFilms = null } = filmBundle || {};
    const finalIsForum = isForum || productType === 'forum';
    const isBundle = bundleFilms !== null && bundleFilms.length > 0;
    const isEvent = !finalIsForum && type === 'event';
    const isScreening = type === 'screening';

    const subs = subtitleLanguages || filmSubtitles || null;

    const { startDate, endDate, endIsSameDay } = useParseDates(itemStartDate, itemEndDate);

    const { setModal } = useModal();

    const onClickLink = useCallback(
        (e) => {
            if (openFilmModal !== null && isModal) {
                e.preventDefault();
                openFilmModal(film);
            } else if (isModal) {
                e.preventDefault();
                setModal({ type: 'film', item: film });
            }
        },
        [openFilmModal, item, isModal],
    );

    return (
        <CardLayout
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.isForum]: finalIsForum,
                    [styles.isScreening]: isScreening,
                    [styles.isEvent]: isEvent,
                },
            ])}
            type="event"
            isWide
            url={url}
            onClick={isFilm ? onClickLink : null}
            image={image}
            placeholderIndex={placeholderIndex}
            tags={
                <CardTags
                    withOnline={isOnline}
                    withFree={isFree}
                    withInvitationOnly={isInvitationOnly}
                    year={withYear && year !== null ? year : null}
                    withQA={qa !== null}
                />
            }
        >
            <div className={styles.content}>
                <div className={styles.info}>
                    <p className={classNames(styles.heading, styles.eventType)}>
                        {isScreening ? (
                            <FormattedMessage
                                defaultMessage="Screening"
                                description="Entity type heading"
                            />
                        ) : null}
                        {finalIsForum ? (
                            <FormattedMessage
                                defaultMessage="Forum"
                                description="Entity type heading"
                            />
                        ) : null}
                        {isEvent ? (
                            <FormattedMessage
                                defaultMessage="Event"
                                description="Entity type heading"
                            />
                        ) : null}
                    </p>
                    {type === 'screening' && film !== null ? (
                        <FilmTitle
                            className={styles.filmInfo}
                            linkClassName={styles.link}
                            titleClassName={styles.title}
                            alternateTitleClassName={styles.alternateTitle}
                            titlesContainerClassName={styles.titlesContainer}
                            metaItemClassName={styles.meta}
                            directorsClassName={styles.directors}
                            item={film}
                            url={url}
                            onClick={isFilm ? onClickLink : null}
                            isLink
                            withCaptions={subs !== null && subs.length > 0}
                        />
                    ) : (
                        <>
                            {isLink ? (
                                <h2 className={styles.title}>
                                    <Link
                                        className={styles.link}
                                        href={url}
                                        onClick={isFilm ? onClickLink : null}
                                    >
                                        {title}
                                    </Link>
                                </h2>
                            ) : (
                                <h2 className={styles.title}>{title}</h2>
                            )}
                        </>
                    )}

                    {showDateRange ? (
                        <DateRange
                            className={styles.heading}
                            startDate={startDate}
                            endDate={itemEndDate}
                        />
                    ) : null}

                    {isBundle ? (
                        <div className={styles.bundle}>
                            <ul className={styles.bundleList}>
                                {bundleFilms.map(
                                    (
                                        { url: bundleFilmUrl = null, title: filmTitle = null },
                                        idx,
                                    ) => (
                                        <li key={`${url}-${idx + 1}`}>
                                            <Link
                                                href={bundleFilmUrl}
                                                className={classNames(
                                                    styles.heading,
                                                    styles.bundleFilm,
                                                    styles.link,
                                                )}
                                            >
                                                {filmTitle}
                                            </Link>
                                        </li>
                                    ),
                                )}
                            </ul>
                        </div>
                    ) : null}
                </div>
                <div className={styles.buttons}>
                    {isUpcoming || withFavoriteButton ? (
                        <div className={styles.cardButtonsTop}>
                            <AddToFavoritesButton
                                className={classNames(styles.actionButton, styles.favouriteButton)}
                                iconClassName={styles.actionButtonIcon}
                                item={item}
                                secondary
                                withoutLabel
                            />
                        </div>
                    ) : null}
                    <div
                        className={classNames([styles.right], {
                            [styles.buttonEnd]: isEvent,
                        })}
                    >
                        {showDate && venue !== null ? (
                            <div className={styles.timeAndPlace}>
                                {venue !== null ? (
                                    <h3 className={classNames(styles.heading, styles.venue)}>
                                        <span className={styles.venueName}>{venue.name}</span>
                                    </h3>
                                ) : null}
                                <div className={styles.dateTimes}>
                                    {itemEndDate !== null && endDate !== null ? (
                                        endIsSameDay ? (
                                            <>
                                                <Date
                                                    date={startDate}
                                                    className={styles.date}
                                                    withoutTime
                                                />
                                                <div className={styles.times}>
                                                    <Time
                                                        dateTime={startDate}
                                                        className={styles.time}
                                                    />
                                                    &nbsp;
                                                    <span className={styles.separator}>-</span>
                                                    &nbsp;
                                                    <Time
                                                        dateTime={endDate}
                                                        className={classNames([
                                                            styles.time,
                                                            styles.dark,
                                                        ])}
                                                    />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <Date
                                                    date={startDate}
                                                    className={styles.date}
                                                    withoutTime
                                                />
                                                <div className={styles.times}>
                                                    <Time
                                                        dateTime={startDate}
                                                        className={styles.time}
                                                    />
                                                    &nbsp;
                                                    <span className={styles.separator}>-</span>
                                                    &nbsp;
                                                    <Time
                                                        dateTime={endDate}
                                                        className={classNames([
                                                            styles.time,
                                                            styles.dark,
                                                        ])}
                                                    />
                                                </div>
                                                <Date
                                                    date={endDate}
                                                    className={classNames([
                                                        styles.date,
                                                        styles.dark,
                                                        styles.after,
                                                    ])}
                                                    withoutTime
                                                />
                                            </>
                                        )
                                    ) : (
                                        <>
                                            <Date
                                                date={startDate}
                                                className={styles.date}
                                                withoutTime
                                            />
                                            <Time dateTime={startDate} className={styles.time} />
                                        </>
                                    )}
                                </div>
                            </div>
                        ) : null}
                        {!isPurchasable || (!isUpcoming && !isOnline) ? (
                            <IconLink
                                href={url}
                                icon={<CircledPlus />}
                                iconPosition="left"
                                className={classNames([
                                    styles.viewButton,
                                    { [styles.buttonEnd]: !showDate && venue === null },
                                ])}
                            >
                                <FormattedMessage
                                    defaultMessage="View details"
                                    description="Button label"
                                />
                            </IconLink>
                        ) : null}
                        {isPurchasable && isUpcoming && ticketsUrl !== null ? (
                            <TicketButton
                                href={ticketsUrl}
                                external
                                className={styles.ticketButton}
                            >
                                {finalIsForum ? (
                                    <FormattedMessage
                                        defaultMessage="Get a pass"
                                        description="Button label"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Buy tickets"
                                        description="Button label"
                                    />
                                )}
                            </TicketButton>
                        ) : null}
                    </div>
                </div>
            </div>
        </CardLayout>
    );
};

EventCard.propTypes = propTypes;
EventCard.defaultProps = defaultProps;

export default EventCard;
